/**
 * Topic Helper Functions
 * Utility functions for topic normalization and validation
 */

// Topic type constants
export const TOPIC_TYPES = {
  MAIN: true,
  DIAGNOSTIC: false
};

/**
 * Normalize topic object from network picker to internal format
 * @param {Object} topic - Topic from network picker (has Name property)
 * @param {boolean} isMain - Whether this is a main topic
 * @returns {Object} Normalized topic object with Desc property
 */
export const normalizeTopic = (topic, isMain = false) => {
  if (!topic || !topic.ID) {
    throw new Error('Invalid topic: missing ID');
  }

  return {
    ID: topic.ID,
    Desc: topic.Name || topic.Desc || '',
    IsMain: isMain
  };
};

/**
 * Validate topic object structure
 * @param {Object} topic - Topic to validate
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export const validateTopic = (topic) => {
  const errors = [];

  if (!topic) {
    errors.push('Topic is required');
    return { valid: false, errors };
  }

  if (!topic.ID) {
    errors.push('Topic ID is required');
  }

  if (!topic.Desc && !topic.Name) {
    errors.push('Topic description/name is required');
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Check if topic exists in topics array
 * @param {Array} topics - Array of topics
 * @param {string|number} topicId - Topic ID to find
 * @returns {Object|null} Existing topic or null
 */
export const findTopic = (topics, topicId) => {
  return topics.find(t => t.ID === topicId) || null;
};

/**
 * Check if main topic exists
 * @param {Array} topics - Array of topics
 * @returns {Object|null} Main topic or null
 */
export const findMainTopic = (topics) => {
  return topics.find(t => t.IsMain === TOPIC_TYPES.MAIN) || null;
};

/**
 * Create immutable update of topics array
 * @param {Array} topics - Current topics array
 * @param {Function} updater - Function that receives topics and returns updated array
 * @returns {Array} New topics array
 */
export const updateTopics = (topics, updater) => {
  const currentTopics = [...(topics || [])];
  return updater(currentTopics);
};

/**
 * Add topic to topics array (immutable)
 * @param {Array} topics - Current topics array
 * @param {Object} topic - Topic to add
 * @returns {Array} New topics array with topic added
 */
export const addTopic = (topics, topic) => {
  return [...(topics || []), topic];
};

/**
 * Remove topic from topics array (immutable)
 * @param {Array} topics - Current topics array
 * @param {string|number} topicId - Topic ID to remove
 * @returns {Array} New topics array with topic removed
 */
export const removeTopic = (topics, topicId) => {
  return (topics || []).filter(t => t.ID !== topicId);
};

/**
 * Update topic in topics array (immutable)
 * @param {Array} topics - Current topics array
 * @param {string|number} topicId - Topic ID to update
 * @param {Object} updates - Properties to update
 * @returns {Array} New topics array with topic updated
 */
export const updateTopic = (topics, topicId, updates) => {
  return (topics || []).map(t => 
    t.ID === topicId ? { ...t, ...updates } : t
  );
};

/**
 * Replace topic in topics array (immutable)
 * @param {Array} topics - Current topics array
 * @param {string|number} topicId - Topic ID to replace
 * @param {Object} newTopic - New topic object
 * @returns {Array} New topics array with topic replaced
 */
export const replaceTopic = (topics, topicId, newTopic) => {
  return (topics || []).map(t => 
    t.ID === topicId ? newTopic : t
  );
};

/**
 * Remove all main topics except the specified one (immutable)
 * @param {Array} topics - Current topics array
 * @param {string|number} keepTopicId - Topic ID to keep as main
 * @returns {Array} New topics array with only one main topic
 */
export const ensureSingleMainTopic = (topics, keepTopicId) => {
  return (topics || []).map(t => {
    if (t.IsMain === TOPIC_TYPES.MAIN && t.ID !== keepTopicId) {
      return { ...t, IsMain: TOPIC_TYPES.DIAGNOSTIC };
    }
    return t;
  });
};

/**
 * Remove duplicate topics (immutable)
 * For main topics: keep only the first one (should only be one anyway)
 * For diagnostic topics: keep only the first occurrence of each topic ID
 * @param {Array} topics - Current topics array
 * @returns {Array} New topics array with duplicates removed
 */
export const removeDuplicateTopics = (topics) => {
  const seenMain = new Set();
  const seenDiagnostic = new Set();
  
  return (topics || []).filter(t => {
    if (t.IsMain === TOPIC_TYPES.MAIN) {
      if (seenMain.has(t.ID)) {
        return false; // Duplicate main topic
      }
      seenMain.add(t.ID);
      return true;
    } else {
      if (seenDiagnostic.has(t.ID)) {
        return false; // Duplicate diagnostic topic
      }
      seenDiagnostic.add(t.ID);
      return true;
    }
  });
};